/**
 * Enum value sets shared by the web app, the API, and the database schema.
 * The Drizzle pgEnum definitions are derived from these arrays, so the three
 * layers cannot drift.
 */

// --- Community & membership ---

export const COMMUNITY_TYPES = [
  'university',
  'employer',
  'health_plan',
  'nonprofit',
  'public',
] as const;
export type CommunityType = (typeof COMMUNITY_TYPES)[number];

export const COMMUNITY_STATUSES = ['active', 'inactive', 'pending'] as const;
export type CommunityStatus = (typeof COMMUNITY_STATUSES)[number];

export const MEMBERSHIP_STATUSES = [
  'pending',
  'active',
  'suspended',
  'removed',
  'graduated',
] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const VERIFICATION_METHODS = [
  'email_domain',
  'sso_saml',
  'sso_oidc',
  'roster',
  'invite_code',
  'idv',
] as const;
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number];

// --- User & age assurance ---

export const USER_STATUSES = ['active', 'hold', 'suspended', 'banned', 'deleted'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const AGE_VERIFICATION_STATUSES = [
  'unverified',
  'pending',
  'verified_adult',
  'rejected_minor',
  'under_review',
] as const;
export type AgeVerificationStatus = (typeof AGE_VERIFICATION_STATUSES)[number];

export const AGE_VERIFICATION_LAYERS = [
  'self_attested',
  'app_store_signal',
  'facial_estimation',
  'id_document',
  'behavioral',
] as const;
export type AgeVerificationLayer = (typeof AGE_VERIFICATION_LAYERS)[number];

export const AGE_VERIFICATION_RESULTS = ['pass', 'fail', 'buffer_zone', 'under_review'] as const;
export type AgeVerificationResult = (typeof AGE_VERIFICATION_RESULTS)[number];

// --- Consent ---

export const CONSENT_TYPES = ['platform_tos', 'safety_acknowledgment'] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];

// --- Conversations & messages ---

export const CONVERSATION_STATUSES = ['active', 'paused', 'ended', 'graduated', 'dead'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

export const MESSAGE_MODERATION_STATUSES = ['pending', 'cleared', 'flagged', 'blocked'] as const;
export type MessageModerationStatus = (typeof MESSAGE_MODERATION_STATUSES)[number];

// Tapback-style message reactions. A deliberately fixed, curated palette
// (not a full keyboard): warm/curious/empathetic glyphs only — no
// thumbs-down, no laugh-cry, no fire/skull. A bounded allowlist is the
// safety surface: no slur, no slur-pun, no novel glyph can land. Ten
// glyphs gives enough range without inviting the moderation problem a
// free-form picker would.
export const REACTION_EMOJIS = ['❤️', '🤗', '🫂', '🥺', '👍', '✨', '🌱', '☕', '🕯️', '🤔'] as const;
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

// --- Moderation ---

export const MODERATION_SOURCES = [
  'openai_moderation',
  'llm_pass',
  'crisis_detector',
  'user_report',
  'behavioral',
] as const;
export type ModerationSource = (typeof MODERATION_SOURCES)[number];

export const MODERATION_CATEGORIES = [
  'harassment',
  'hate',
  'sexual',
  'violence',
  'self_harm',
  'grooming',
  'scam',
  'off_platform_contact',
  'minor_indicators',
  'meetup_intent',
  // CSAM-suspected = compound (sexual + minor indicators in the same
  // message). Triggers mandatory NCMEC CyberTipline reporting under the
  // REPORT Act, instant ban of the sender, and 1-year evidence retention.
  'csam_suspected',
  'spam',
  'other',
] as const;
export type ModerationCategory = (typeof MODERATION_CATEGORIES)[number];

export const MODERATION_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type ModerationSeverity = (typeof MODERATION_SEVERITIES)[number];

export const MODERATION_EVENT_STATUSES = ['open', 'in_review', 'actioned', 'dismissed'] as const;
export type ModerationEventStatus = (typeof MODERATION_EVENT_STATUSES)[number];
